import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calculator,
  Download,
  Shield,
  PieChart,
  Building,
  Archive,
  FileText,
  TrendingUp,
  AlertTriangle,
  X,
  BarChart3,
  ArrowRightLeft,
  ChevronUp,
  ChevronDown,
  Eye,
  AlertCircle,
} from 'lucide-react';
import { getSpecializedSectorData } from '../../shared/utils/comptabiliteService';
import { logger } from '../../shared/utils/logger';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';

const DEFAULT_SECTORS_DATA = {
  overview: {
    secteur: 'Assurances',
    reglementation: 'Loi 06-04 / UAR',
    exercice: '2024',
    conformity: 87,
  },
  assurances: {
    portefeuille: {
      vieCapitalisation: {
        encours: 125000000,
        nouvelleProd: 18500000,
        rachatsSinistres: 8200000,
        resultatTechnique: 4500000,
      },
      nonVie: {
        encours: 78000000,
        nouvelleProd: 22000000,
        rachatsSinistres: 16500000,
        resultatTechnique: -2100000,
      },
    },
    provisions: {
      mathematiques: 98500000,
      sinistresAPayer: 42100000,
      egalisationRisques: 12800000,
      participationBenefices: 5600000,
    },
    reassurance: {
      primsEmises: 14200000,
      primsAcceptees: 8700000,
      commissionsRecues: 2100000,
      soldeReassureurs: 3400000,
    },
  },
  consolidation: {
    perimetre: [
      {
        entiteKey: 'demo_entity_algerie',
        activiteKey: 'demo_activity_algerie',
        pourcentageDetention: 100,
        integrationMethodKey: 'integration_global',
        capitalSocial: 500000000,
        resultatNet: 12500000,
      },
      {
        entiteKey: 'demo_entity_teleassurance',
        activiteKey: 'demo_activity_teleassurance',
        pourcentageDetention: 72,
        integrationMethodKey: 'integration_global',
        capitalSocial: 50000000,
        resultatNet: 3200000,
      },
      {
        entiteKey: 'demo_entity_reassurance',
        activiteKey: 'demo_activity_reassurance',
        pourcentageDetention: 35,
        integrationMethodKey: 'integration_equity',
        capitalSocial: 120000000,
        resultatNet: 1800000,
      },
    ],
    eliminations: [
      { typeKey: 'demo_elim_intragroup_premiums', montant: 4500000 },
      { typeKey: 'demo_elim_reciprocal_receivables', montant: 2800000 },
      { typeKey: 'demo_elim_internal_gains', montant: 1200000 },
    ],
    bilanConsolide: {
      actif: {
        immobilisations: 45000000,
        placementsFinanciers: 198000000,
        creancesAssures: 62000000,
        reassueurs: 18000000,
        tresorerie: 35000000,
      },
      passif: {
        capitauxPropres: 89000000,
        provisionsTechniques: 159000000,
        dettesAssures: 42000000,
        autresDettes: 28000000,
      },
    },
  },
  etatsSpecialises: {
    bilanAssurances: {
      actif: [
        { posteKey: 'demo_poste_financial_investments', montant: 198000000, pourcentage: 52 },
        { posteKey: 'demo_poste_contract_receivables', montant: 62000000, pourcentage: 16 },
        { posteKey: 'demo_poste_reinsurer_share', montant: 18000000, pourcentage: 5 },
        { posteKey: 'demo_poste_tangible_assets', montant: 45000000, pourcentage: 12 },
        { posteKey: 'demo_poste_cash', montant: 35000000, pourcentage: 9 },
      ],
      passif: [
        { posteKey: 'demo_poste_technical_provisions', montant: 159000000, pourcentage: 42 },
        { posteKey: 'demo_poste_equity', montant: 89000000, pourcentage: 23 },
        { posteKey: 'demo_poste_policyholder_debts', montant: 42000000, pourcentage: 11 },
        { posteKey: 'demo_poste_other_debts', montant: 28000000, pourcentage: 7 },
        { posteKey: 'demo_poste_risk_provisions', montant: 62000000, pourcentage: 16 },
      ],
    },
    compteExploitation: [
      { posteKey: 'demo_poste_premiums_earned', montant: 38500000, nature: 'produit' },
      { posteKey: 'demo_poste_claims_charges', montant: -22100000, nature: 'charge' },
      { posteKey: 'demo_poste_acquisition_charges', montant: -6800000, nature: 'charge' },
      { posteKey: 'demo_poste_admin_charges', montant: -4200000, nature: 'charge' },
      { posteKey: 'demo_poste_technical_result', montant: 5400000, nature: 'resultat' },
      { posteKey: 'demo_poste_financial_income', montant: 8900000, nature: 'produit' },
      { posteKey: 'demo_poste_net_result', montant: 12500000, nature: 'resultat' },
    ],
  },
  surveillance: {
    ratiosReglementaires: [
      { ratioKey: 'demo_ratio_solvency', seuil: 20, valeur: 28, statut: 'conforme', categorie: 'solvabilite' },
      { ratioKey: 'demo_ratio_risk_coverage', seuil: 100, valeur: 112, statut: 'conforme', categorie: 'solvabilite' },
      { ratioKey: 'demo_ratio_liquidity', seuil: 40, valeur: 38, statut: 'alerte', categorie: 'liquidite' },
      { ratioKey: 'demo_ratio_equity', seuil: 10, valeur: 14, statut: 'conforme', categorie: 'liquidite' },
    ],
    controles: [
      { typeKey: 'demo_control_on_site', date: '2024-09-15', resultat: 'Conforme' },
      { typeKey: 'demo_control_external_audit', date: '2024-11-22', resultat: 'Conforme' },
      { typeKey: 'demo_control_provisions_review', date: '2024-12-05', resultat: 'Observations mineures' },
    ],
  },
};

const DEFAULT_RATIOS_MODAL_SECTIONS = { overview: true, detail: false };
const DEFAULT_ETATS_MODAL_SECTIONS = { primary: true, detail: false };

const pct = (part, total) => (total > 0 ? ((part / total) * 100).toFixed(1) : '0.0');

const sumProvisions = (provisions = {}) =>
  Object.values(provisions).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);

const mergeSectorApiData = (prev, apiRows) => {
  if (!apiRows?.length) return prev;
  const apiData = apiRows.find((r) => r.data && Object.keys(r.data).length > 0)?.data;
  if (!apiData) return prev;
  return {
    ...prev,
    overview: { ...prev.overview, ...apiData.overview },
    assurances: {
      ...prev.assurances,
      ...apiData.assurances,
      portefeuille: {
        ...prev.assurances?.portefeuille,
        ...apiData.assurances?.portefeuille,
        vieCapitalisation: { ...prev.assurances?.portefeuille?.vieCapitalisation, ...apiData.assurances?.portefeuille?.vieCapitalisation },
        nonVie: { ...prev.assurances?.portefeuille?.nonVie, ...apiData.assurances?.portefeuille?.nonVie },
      },
      provisions: { ...prev.assurances?.provisions, ...apiData.assurances?.provisions },
      reassurance: { ...prev.assurances?.reassurance, ...apiData.assurances?.reassurance },
    },
    consolidation: { ...prev.consolidation, ...apiData.consolidation },
    etatsSpecialises: { ...prev.etatsSpecialises, ...apiData.etatsSpecialises },
    surveillance: { ...prev.surveillance, ...apiData.surveillance },
  };
};

const EmptyTabState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-16 bg-white/5 rounded-xl border-2 border-dashed border-white/10">
    <Icon className="w-12 h-12 mx-auto mb-4 text-white/30" />
    <p className="text-white/80 font-medium">{title}</p>
    <p className="text-white/50 text-sm mt-2 max-w-md mx-auto">{description}</p>
  </div>
);

const SecteursSpecialisesPage = () => {
  const { t, i18n } = useTranslation();
  const numberLocale = getBrandLocale(i18n.language);
  const p = (key, opts) => t(`modules.comptabilite.pages.secteursSpecialisesPage.${key}`, opts);
  const demoLabel = useCallback((key, fallback) => (key ? p(key) : fallback), [p]);
  const posteLabel = useCallback((item) => demoLabel(item.posteKey, item.poste), [demoLabel]);
  const entityLabel = useCallback((item) => demoLabel(item.entiteKey, item.entite), [demoLabel]);
  const activityLabel = useCallback((item) => demoLabel(item.activiteKey, item.activite), [demoLabel]);
  const ratioLabel = useCallback((item) => demoLabel(item.ratioKey, item.ratio), [demoLabel]);
  const controlTypeLabel = useCallback((item) => demoLabel(item.typeKey, item.type), [demoLabel]);

  const getRatioStatusLabel = useCallback((statut) => {
    if (statut === 'conforme') return p('status_compliant');
    if (statut === 'alerte') return p('status_alert');
    return p('status_non_compliant');
  }, [t]);

  const translateIntegrationMethod = useCallback((item) => {
    const key = typeof item === 'object' ? (item?.integrationMethodKey || item?.methodeIntegration) : item;
    if (typeof key === 'string' && key.startsWith('integration_')) return p(key);
    return typeof key === 'string' ? key : '';
  }, [p]);

  const translateControlResult = useCallback((value) => {
    const map = {
      Conforme: p('control_result_compliant'),
      'Observations mineures': p('control_result_minor_observations'),
    };
    return map[value] || value;
  }, [t]);

  const provisionLabels = useMemo(() => ({
    mathematiques: p('prov_mathematiques'),
    sinistresAPayer: p('prov_sinistres'),
    egalisationRisques: p('prov_egalisation'),
    participationBenefices: p('prov_participation'),
    autres: p('prov_autres'),
  }), [t]);

  const bilanActifLabels = useMemo(() => ({
    immobilisations: p('bilan_immobilisations'),
    placementsFinanciers: p('bilan_placements'),
    creancesAssures: p('bilan_creances'),
    reassueurs: p('bilan_reassureurs'),
    tresorerie: p('bilan_tresorerie'),
  }), [t]);

  const bilanPassifLabels = useMemo(() => ({
    capitauxPropres: p('bilan_capitaux'),
    provisionsTechniques: p('bilan_provisions_tech'),
    dettesAssures: p('bilan_dettes_assures'),
    autresDettes: p('bilan_autres_dettes'),
  }), [t]);

  const etatsConsolidesCards = useMemo(() => [
    { id: 'bilan', icon: BarChart3, glow: 'from-blue-500 to-cyan-600', iconClass: 'text-blue-400', title: p('card_bilan_title'), sub: p('card_bilan_sub') },
    { id: 'resultat', icon: TrendingUp, glow: 'from-green-500 to-emerald-600', iconClass: 'text-green-400', title: p('card_resultat_title'), sub: p('card_resultat_sub') },
    { id: 'flux', icon: ArrowRightLeft, glow: 'from-purple-500 to-pink-600', iconClass: 'text-purple-400', title: p('card_flux_title'), sub: p('card_flux_sub') },
    { id: 'annexe', icon: FileText, glow: 'from-orange-500 to-red-600', iconClass: 'text-orange-400', title: p('card_annexe_title'), sub: p('card_annexe_sub') },
  ], [t]);

  const fluxItems = useMemo(() => [
    { poste: p('flux_operating'), montant: 18500000 },
    { poste: p('flux_investment'), montant: -9200000 },
    { poste: p('flux_financing'), montant: -4100000 },
    { poste: p('flux_net_change'), montant: 5200000 },
  ], [t]);

  const tabs = useMemo(() => [
    { id: 'portefeuille', label: p('tab_portfolio'), icon: PieChart },
    { id: 'provisions', label: p('tab_provisions'), icon: Archive },
    { id: 'consolidation', label: p('tab_consolidation'), icon: Building },
    { id: 'etats-specialises', label: p('tab_specialized_statements'), icon: FileText },
    { id: 'surveillance', label: p('tab_prudential_surveillance'), icon: Shield },
  ], [t]);

  const [sectorsData, setSectorsData] = useState(DEFAULT_SECTORS_DATA);
  const [sectorsActiveTab, setSectorsActiveTab] = useState('portefeuille');
  const [showRatiosPrudentiels, setShowRatiosPrudentiels] = useState(false);
  const [showEtatsConsolides, setShowEtatsConsolides] = useState(false);
  const [selectedEtatType, setSelectedEtatType] = useState(null);
  const [ratiosExpandedSections, setRatiosExpandedSections] = useState(DEFAULT_RATIOS_MODAL_SECTIONS);
  const [etatsExpandedSections, setEtatsExpandedSections] = useState(DEFAULT_ETATS_MODAL_SECTIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const vieEncours = sectorsData.assurances?.portefeuille?.vieCapitalisation?.encours || 0;
  const nonVieEncours = sectorsData.assurances?.portefeuille?.nonVie?.encours || 0;
  const totalEncours = vieEncours + nonVieEncours;
  const perimetre = sectorsData.consolidation?.perimetre || [];
  const ratios = sectorsData.surveillance?.ratiosReglementaires || [];
  const ratiosConformes = ratios.filter((r) => r.statut === 'conforme').length;

  const openRatiosModal = () => {
    setRatiosExpandedSections(DEFAULT_RATIOS_MODAL_SECTIONS);
    setShowRatiosPrudentiels(true);
  };

  const openEtatsModal = () => {
    setSelectedEtatType(null);
    setEtatsExpandedSections(DEFAULT_ETATS_MODAL_SECTIONS);
    setShowEtatsConsolides(true);
  };

  const handleSelectEtat = (etatId) => {
    setSelectedEtatType(etatId);
    setEtatsExpandedSections({ primary: false, detail: true });
  };

  const goToTab = (tabId, etatId) => {
    setShowRatiosPrudentiels(false);
    setShowEtatsConsolides(false);
    setSectorsActiveTab(tabId);
    if (etatId) setSelectedEtatType(etatId);
  };

  const handleExportEtat = async (etatId) => {
    const card = etatsConsolidesCards.find((c) => c.id === etatId);
    if (!card) return;
    try {
      let rows = [];
      if (etatId === 'bilan') {
        const actif = sectorsData.consolidation?.bilanConsolide?.actif || {};
        const passif = sectorsData.consolidation?.bilanConsolide?.passif || {};
        rows = [
          ...Object.entries(actif).map(([k, v]) => ({ [p('pdf_section')]: p('pdf_section_actif'), [p('pdf_poste')]: bilanActifLabels[k] || k, [p('pdf_amount')]: v })),
          ...Object.entries(passif).map(([k, v]) => ({ [p('pdf_section')]: p('pdf_section_passif'), [p('pdf_poste')]: bilanPassifLabels[k] || k, [p('pdf_amount')]: v })),
        ];
      } else if (etatId === 'resultat') {
        rows = (sectorsData.etatsSpecialises?.compteExploitation || []).map((r) => ({
          [p('pdf_poste')]: posteLabel(r), [p('pdf_amount')]: r.montant, [p('pdf_nature')]: r.nature,
        }));
      } else if (etatId === 'flux') {
        rows = fluxItems.map((item) => ({
          [p('pdf_poste')]: posteLabel(item), [p('pdf_amount')]: item.montant,
        }));
      } else if (etatId === 'annexe') {
        rows = perimetre.map((e) => ({
          [p('pdf_entity')]: entityLabel(e), [p('pdf_ownership')]: `${e.pourcentageDetention}%`, [p('pdf_method')]: translateIntegrationMethod(e.integrationMethodKey || e.methodeIntegration), [p('pdf_net_result')]: e.resultatNet,
        }));
      }
      await generateReportPDF(p('export_consolidated_prefix', { title: card.title }), rows, { orientation: 'landscape' });
    } catch (error) {
      logger.error('Export état consolidé:', error);
    }
  };

  const handleExportRatios = async () => {
    try {
      const rows = ratios.map((r) => ({
        [p('pdf_ratio')]: ratioLabel(r), [p('pdf_value')]: `${r.valeur}%`, [p('pdf_threshold')]: `${r.seuil}%`, [p('pdf_status')]: getRatioStatusLabel(r.statut),
      }));
      await generateReportPDF(p('export_ratios_title'), rows);
    } catch (error) {
      logger.error('Export ratios:', error);
    }
  };

  const renderEtatDetailContent = () => {
    if (!selectedEtatType) {
      return (
        <p className="text-white/50 text-sm text-center py-8">
          {p('select_consolidated_hint')}
        </p>
      );
    }
    if (selectedEtatType === 'bilan') {
      const actif = sectorsData.consolidation?.bilanConsolide?.actif || {};
      const passif = sectorsData.consolidation?.bilanConsolide?.passif || {};
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <h5 className="text-white/80 font-medium mb-2">{p('actif')}</h5>
            <div className="space-y-2">
              {Object.entries(actif).map(([key, value]) => (
                <div key={key} className="flex justify-between bg-white/5 rounded-lg p-3">
                  <span className="text-white/70">{bilanActifLabels[key] || key}</span>
                  <span className="text-blue-400 font-mono">{value.toLocaleString(numberLocale)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h5 className="text-white/80 font-medium mb-2">{p('passif')}</h5>
            <div className="space-y-2">
              {Object.entries(passif).map(([key, value]) => (
                <div key={key} className="flex justify-between bg-white/5 rounded-lg p-3">
                  <span className="text-white/70">{bilanPassifLabels[key] || key}</span>
                  <span className="text-green-400 font-mono">{value.toLocaleString(numberLocale)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    if (selectedEtatType === 'resultat') {
      return (
        <div className="space-y-2">
          {(sectorsData.etatsSpecialises?.compteExploitation || []).map((item, index) => (
            <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-3">
              <span className="text-white">{posteLabel(item)}</span>
              <span className={`font-bold font-mono ${
                item.nature === 'produit' ? 'text-green-400' : item.nature === 'charge' ? 'text-red-400' : 'text-blue-400'
              }`}>
                {item.montant >= 0 && item.nature !== 'charge' ? '+' : ''}{item.montant.toLocaleString(numberLocale)} {getBrandCurrencySuffix()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    if (selectedEtatType === 'flux') {
      return (
        <div className="space-y-2">
          {fluxItems.map((item, index) => (
            <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-3">
              <span className="text-white">{posteLabel(item)}</span>
              <span className={`font-bold font-mono ${item.montant >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {item.montant >= 0 ? '+' : ''}{item.montant.toLocaleString(numberLocale)} {getBrandCurrencySuffix()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    if (selectedEtatType === 'annexe') {
      return (
        <div className="space-y-3">
          {perimetre.map((entite, index) => (
            <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex justify-between flex-wrap gap-2 mb-2">
                <span className="text-white font-medium">{entityLabel(entite)}</span>
                <span className="text-pink-400 text-sm">{p('ownership_short', { pct: entite.pourcentageDetention })}</span>
              </div>
              <p className="text-white/60 text-sm">{activityLabel(entite)} — {translateIntegrationMethod(entite.integrationMethodKey || entite.methodeIntegration)}</p>
              <p className="text-white/50 text-xs mt-1">{p('net_result_label')} {entite.resultatNet.toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</p>
            </div>
          ))}
          {(sectorsData.consolidation?.eliminations || []).length > 0 && (
            <div className="mt-4">
              <h5 className="text-white/80 font-medium mb-2">{p('eliminations')}</h5>
              {(sectorsData.consolidation?.eliminations || []).map((e, i) => (
                <div key={i} className="flex justify-between bg-white/5 rounded-lg p-2 mb-1">
                  <span className="text-white/70 text-sm">{e.type}</span>
                  <span className="text-orange-400 text-sm font-mono">-{e.montant.toLocaleString(numberLocale)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const loadSectorsData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const apiRows = await getSpecializedSectorData('assurance').catch(() => []);
      setSectorsData((prev) => {
        const base = { ...DEFAULT_SECTORS_DATA, ...prev };
        const merged = mergeSectorApiData(base, apiRows);
        const apiRatios = merged.surveillance?.ratiosReglementaires || [];
        const conformes = apiRatios.filter((r) => r.statut === 'conforme').length;
        merged.overview = {
          ...merged.overview,
          conformity: apiRatios.length > 0 ? Math.round((conformes / apiRatios.length) * 100) : merged.overview.conformity,
        };
        return merged;
      });
    } catch (error) {
      logger.error('Error loading specialized sectors:', error);
      setLoadError(p('load_error'));
      setSectorsData(DEFAULT_SECTORS_DATA);
    }
    setIsLoading(false);
  }, [t]);

  useEffect(() => {
    loadSectorsData();
  }, [loadSectorsData]);

  const statsCards = useMemo(() => [
    { title: p('stat_sector_compliance'), value: `${sectorsData.overview.conformity}%`, icon: Shield, color: 'from-green-500 to-green-600' },
    { title: p('stat_total_portfolio'), value: `${(totalEncours / 1000000).toFixed(1)}M ${getBrandCurrencySuffix()}`, icon: PieChart, color: 'from-blue-500 to-blue-600' },
    { title: p('stat_entities_consolidated'), value: perimetre.length, icon: Building, color: 'from-purple-500 to-purple-600' },
    { title: p('stat_technical_provisions'), value: `${(sumProvisions(sectorsData.assurances?.provisions) / 1000000).toFixed(1)}M ${getBrandCurrencySuffix()}`, icon: Archive, color: 'from-orange-500 to-orange-600' },
    { title: p('stat_compliant_ratios'), value: ratios.length ? `${ratiosConformes}/${ratios.length}` : p('em_dash'), icon: Calculator, color: 'from-pink-500 to-rose-600' },
  ], [t, sectorsData.overview.conformity, totalEncours, perimetre.length, sectorsData.assurances?.provisions, ratios.length, ratiosConformes]);

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500" />
        </div>
      );
    }

    if (sectorsActiveTab === 'portefeuille') {
      const vie = sectorsData.assurances?.portefeuille?.vieCapitalisation || {};
      const nonVie = sectorsData.assurances?.portefeuille?.nonVie || {};
      return (
        <div className="space-y-6">
          {loadError && (
            <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 text-orange-200 text-sm">
              <AlertCircle size={16} />
              {loadError}
            </div>
          )}
          <h3 className="text-lg font-semibold text-white mb-4">{p('portfolio_analysis_title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h4 className="text-white font-medium mb-4 flex items-center">
                <TrendingUp className="mr-2" size={20} />
                {p('life_insurance_title')}
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-white/70">{p('label_outstanding')}</span><span className="text-blue-400 font-bold">{(vie.encours || 0).toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</span></div>
                <div className="flex justify-between"><span className="text-white/70">{p('label_new_production')}</span><span className="text-green-400 font-bold">{(vie.nouvelleProd || 0).toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</span></div>
                <div className="flex justify-between"><span className="text-white/70">{p('label_redemptions_claims')}</span><span className="text-red-400 font-bold">-{(vie.rachatsSinistres || 0).toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</span></div>
                <div className="flex justify-between border-t border-white/10 pt-2">
                  <span className="text-white font-medium">{p('label_technical_result')}</span>
                  <span className="text-green-400 font-bold">+{(vie.resultatTechnique || 0).toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</span>
                </div>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h4 className="text-white font-medium mb-4 flex items-center">
                <AlertTriangle className="mr-2" size={20} />
                {p('non_life_insurance_title')}
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-white/70">{p('label_outstanding')}</span><span className="text-blue-400 font-bold">{(nonVie.encours || 0).toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</span></div>
                <div className="flex justify-between"><span className="text-white/70">{p('label_new_production')}</span><span className="text-green-400 font-bold">{(nonVie.nouvelleProd || 0).toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</span></div>
                <div className="flex justify-between"><span className="text-white/70">{p('label_claims')}</span><span className="text-red-400 font-bold">-{(nonVie.rachatsSinistres || 0).toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</span></div>
                <div className="flex justify-between border-t border-white/10 pt-2">
                  <span className="text-white font-medium">{p('label_technical_result')}</span>
                  <span className={`font-bold ${(nonVie.resultatTechnique || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {(nonVie.resultatTechnique || 0).toLocaleString(numberLocale)} {getBrandCurrencySuffix()}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h4 className="text-white font-medium mb-4">{p('portfolio_breakdown')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/70">{p('life_capitalization')}</span>
                  <span className="text-white">{pct(vieEncours, totalEncours)}%</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-400 h-2 rounded-full" style={{ width: `${pct(vieEncours, totalEncours)}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/70">{p('non_life')}</span>
                  <span className="text-white">{pct(nonVieEncours, totalEncours)}%</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div className="bg-gradient-to-r from-orange-500 to-orange-400 h-2 rounded-full" style={{ width: `${pct(nonVieEncours, totalEncours)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectorsActiveTab === 'provisions') {
      const provisions = sectorsData.assurances?.provisions || {};
      const reassurance = sectorsData.assurances?.reassurance || {};
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white mb-4">{p('technical_provisions_title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(provisions).filter(([, v]) => typeof v === 'number').map(([key, value]) => (
              <div key={key} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-white font-medium">{provisionLabels[key] || key}</h4>
                    <p className="text-white/60 text-sm">{p('provisioned_amount')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-white">{value.toLocaleString(numberLocale)}</p>
                    <p className="text-white/60 text-sm">{getBrandCurrencySuffix()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h4 className="text-white font-medium mb-4">{p('reinsurance_title')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center"><p className="text-white/60 text-sm">{p('reinsurance_premiums_issued')}</p><p className="text-xl font-bold text-green-400">{(reassurance.primsEmises || 0).toLocaleString(numberLocale)}</p></div>
              <div className="text-center"><p className="text-white/60 text-sm">{p('reinsurance_premiums_accepted')}</p><p className="text-xl font-bold text-blue-400">{(reassurance.primsAcceptees || 0).toLocaleString(numberLocale)}</p></div>
              <div className="text-center"><p className="text-white/60 text-sm">{p('reinsurance_commissions_received')}</p><p className="text-xl font-bold text-yellow-400">{(reassurance.commissionsRecues || 0).toLocaleString(numberLocale)}</p></div>
              <div className="text-center"><p className="text-white/60 text-sm">{p('reinsurance_balance')}</p><p className="text-xl font-bold text-orange-400">{(reassurance.soldeReassureurs || 0).toLocaleString(numberLocale)}</p></div>
            </div>
          </div>
        </div>
      );
    }

    if (sectorsActiveTab === 'consolidation') {
      return (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white mb-4">{p('consolidation_scope')}</h3>
          {perimetre.length === 0 ? (
            <EmptyTabState icon={Building} title={p('empty_consolidation_title')} description={p('empty_consolidation_description')} />
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {perimetre.map((entite, index) => (
                <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                    <div>
                      <h4 className="text-white font-medium">{entityLabel(entite)}</h4>
                      <p className="text-white/60 text-sm">{activityLabel(entite)}</p>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-lg text-sm font-medium">{p('ownership_pct', { pct: entite.pourcentageDetention })}</span>
                      <span className={`px-3 py-1 rounded-lg text-sm font-medium ${(entite.integrationMethodKey || entite.methodeIntegration) === 'integration_global' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {translateIntegrationMethod(entite.integrationMethodKey || entite.methodeIntegration)}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex justify-between"><span className="text-white/70">{p('social_capital')}</span><span className="text-blue-400 font-bold">{entite.capitalSocial.toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</span></div>
                    <div className="flex justify-between">
                      <span className="text-white/70">{p('net_result')}</span>
                      <span className={`font-bold ${entite.resultatNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {entite.resultatNet >= 0 ? '+' : ''}{entite.resultatNet.toLocaleString(numberLocale)} {getBrandCurrencySuffix()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(sectorsData.consolidation?.eliminations || []).length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h4 className="text-white font-medium mb-4">{p('consolidation_eliminations')}</h4>
              <div className="space-y-3">
                {sectorsData.consolidation.eliminations.map((elimination, index) => (
                  <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-3">
                    <span className="text-white">{controlTypeLabel(elimination)}</span>
                    <span className="text-orange-400 font-bold">-{elimination.montant.toLocaleString(numberLocale)} {getBrandCurrencySuffix()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sectorsData.consolidation?.bilanConsolide && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h4 className="text-white font-medium mb-4">{p('consolidated_balance_summary')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h5 className="text-white/80 font-medium mb-3">{p('actif')}</h5>
                  <div className="space-y-2">
                    {Object.entries(sectorsData.consolidation.bilanConsolide.actif || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between bg-white/5 rounded-lg p-2">
                        <span className="text-white/70">{bilanActifLabels[key] || key}:</span>
                        <span className="text-blue-400">{value.toLocaleString(numberLocale)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h5 className="text-white/80 font-medium mb-3">{p('passif')}</h5>
                  <div className="space-y-2">
                    {Object.entries(sectorsData.consolidation.bilanConsolide.passif || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between bg-white/5 rounded-lg p-2">
                        <span className="text-white/70">{bilanPassifLabels[key] || key}:</span>
                        <span className="text-green-400">{value.toLocaleString(numberLocale)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (sectorsActiveTab === 'etats-specialises') {
      const actif = sectorsData.etatsSpecialises?.bilanAssurances?.actif || [];
      const passif = sectorsData.etatsSpecialises?.bilanAssurances?.passif || [];
      const compte = sectorsData.etatsSpecialises?.compteExploitation || [];
      const hasData = actif.length + passif.length + compte.length > 0;
      return (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white mb-4">{p('specialized_insurance_states')}</h3>
          {!hasData ? (
            <EmptyTabState icon={FileText} title={p('empty_specialized_title')} description={p('empty_specialized_description')} />
          ) : (
            <>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h4 className="text-white font-medium mb-4">{p('insurance_balance_sheet')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h5 className="text-white/80 font-medium mb-3">{p('actif')}</h5>
                    <div className="space-y-2">
                      {actif.map((item, index) => (
                        <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-3 gap-2">
                          <div className="min-w-0 flex-1">
                            <span className="text-white font-medium">{posteLabel(item)}</span>
                            <div className="flex items-center mt-1">
                              <div className="w-full bg-white/10 rounded-full h-1 mr-2">
                                <div className="bg-blue-400 h-1 rounded-full" style={{ width: `${Math.min(item.pourcentage, 100)}%` }} />
                              </div>
                              <span className="text-white/60 text-xs">{item.pourcentage}%</span>
                            </div>
                          </div>
                          <span className="text-blue-400 font-bold shrink-0">{item.montant.toLocaleString(numberLocale)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h5 className="text-white/80 font-medium mb-3">{p('passif')}</h5>
                    <div className="space-y-2">
                      {passif.map((item, index) => (
                        <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-3 gap-2">
                          <div className="min-w-0 flex-1">
                            <span className="text-white font-medium">{posteLabel(item)}</span>
                            <div className="flex items-center mt-1">
                              <div className="w-full bg-white/10 rounded-full h-1 mr-2">
                                <div className="bg-green-400 h-1 rounded-full" style={{ width: `${Math.min(item.pourcentage, 100)}%` }} />
                              </div>
                              <span className="text-white/60 text-xs">{item.pourcentage}%</span>
                            </div>
                          </div>
                          <span className="text-green-400 font-bold shrink-0">{item.montant.toLocaleString(numberLocale)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {compte.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="text-white font-medium mb-4">{p('technical_operating_account')}</h4>
                  <div className="space-y-2">
                    {compte.map((item, index) => (
                      <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-3">
                        <span className="text-white">{posteLabel(item)}</span>
                        <span className={`font-bold ${item.nature === 'produit' ? 'text-green-400' : item.nature === 'charge' ? 'text-red-400' : 'text-blue-400'}`}>
                          {item.montant >= 0 && item.nature !== 'charge' ? '+' : ''}{item.montant.toLocaleString(numberLocale)} {getBrandCurrencySuffix()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    if (sectorsActiveTab === 'surveillance') {
      const controles = sectorsData.surveillance?.controles || [];
      return (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white mb-4">{p('prudential_surveillance')}</h3>
          {ratios.length === 0 ? (
            <EmptyTabState icon={Shield} title={p('empty_ratios_title')} description={p('empty_ratios_description')} />
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h4 className="text-white font-medium mb-4">{p('regulatory_ratios')}</h4>
              <div className="space-y-4">
                {ratios.map((ratio, index) => (
                  <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                      <div>
                        <span className="text-white font-medium">{ratioLabel(ratio)}</span>
                        <p className="text-white/60 text-sm">{p('minimum_threshold', { seuil: ratio.seuil })}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-white font-bold text-lg">{ratio.valeur}%</span>
                        <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                          ratio.statut === 'conforme' ? 'bg-green-500/20 text-green-400'
                            : ratio.statut === 'alerte' ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-red-500/20 text-red-400'
                        }`}>
                          {getRatioStatusLabel(ratio.statut)}
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${ratio.statut === 'conforme' ? 'bg-green-400' : ratio.statut === 'alerte' ? 'bg-yellow-400' : 'bg-red-400'}`}
                        style={{ width: `${Math.min((ratio.valeur / (ratio.seuil * 2)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {controles.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h4 className="text-white font-medium mb-4">{p('controls_history')}</h4>
              <div className="space-y-3">
                {controles.map((controle, index) => (
                  <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-4 flex-wrap gap-2">
                    <div>
                      <span className="text-white font-medium">{controlTypeLabel(controle)}</span>
                      <p className="text-white/60 text-sm">{controle.date}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-lg text-sm font-medium ${controle.resultat === 'Conforme' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {translateControlResult(controle.resultat)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const solvabiliteRatios = ratios.filter((r) => r.categorie === 'solvabilite');
  const liquiditeRatios = ratios.filter((r) => r.categorie === 'liquidite');

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">{p('title')}</h2>
            <p className="text-white/60 text-sm">
              {p('subtitle', {
                secteur: sectorsData.overview.secteur,
                reglementation: sectorsData.overview.reglementation,
                exercice: sectorsData.overview.exercice,
              })}
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              type="button"
              onClick={openRatiosModal}
              className="bg-white/10 backdrop-blur-lg border border-white/20 text-white px-4 py-2 rounded-xl font-medium hover:bg-white/15 transition-all duration-300 flex items-center space-x-2"
            >
              <Calculator size={16} />
              <span>{p('btn_ratios_prudentiels')}</span>
            </button>
            <button
              type="button"
              onClick={openEtatsModal}
              className="bg-gradient-to-r from-pink-600 to-rose-600 text-white px-4 py-2 rounded-xl font-medium hover:from-pink-700 hover:to-rose-700 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-pink-500/30"
            >
              <Download size={16} />
              <span>{p('btn_etats_consolides')}</span>
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsCards.slice(0, 4).map((stat, i) => (
              <div key={i} className="relative group">
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white/60 text-sm font-medium">{stat.title}</h3>
                      <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border-b border-white/10 m-2 mb-0">
              <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSectorsActiveTab(tab.id)}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                      sectorsActiveTab === tab.id
                        ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-lg shadow-pink-500/30'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-5 w-5" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="p-6">{renderTabContent()}</div>
          </div>
        </div>
      </div>

      {showRatiosPrudentiels && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowRatiosPrudentiels(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '960px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Calculator className="h-6 w-6 text-green-400" />
                {p('ratios_prudentiels_title')}
              </h2>
              <button type="button" onClick={() => setShowRatiosPrudentiels(false)} className="text-white/70 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1 space-y-4">
              <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                <button type="button" onClick={() => setRatiosExpandedSections((p) => ({ ...p, overview: !p.overview }))} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-3">
                    <Shield size={20} className="text-green-400" />
                    <h3 className="text-lg font-semibold text-white">{p('overview')}</h3>
                  </div>
                  {ratiosExpandedSections.overview ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                </button>
                <AnimatePresence>
                  {ratiosExpandedSections.overview && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="p-4 pt-0 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                          {[
                            { title: p('ratios_tracked'), value: ratios.length, glow: 'from-blue-500 to-blue-600' },
                            { title: p('ratios_compliant'), value: ratiosConformes, glow: 'from-green-500 to-green-600' },
                            { title: p('global_compliance'), value: `${sectorsData.overview.conformity}%`, glow: 'from-purple-500 to-purple-600' },
                          ].slice(0, 4).map((stat, i) => (
                            <div key={i} className="relative group">
                              <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.glow} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                              <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                                <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-3">{p('solvency_ratios')}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {(solvabiliteRatios.length ? solvabiliteRatios : ratios.slice(0, 2)).map((ratio, i) => (
                              <div key={i} className="relative group">
                                <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm" style={{ pointerEvents: 'none' }} />
                                <div className="relative bg-gradient-to-r from-green-500/10 to-emerald-500/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20">
                                  <div className="text-white/60 text-sm">{ratioLabel(ratio)}</div>
                                  <div className="text-2xl font-bold text-white">{ratio.valeur}%</div>
                                  <div className="text-xs text-white/50 mt-1">{p('minimum_required', { seuil: ratio.seuil })}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-3">{p('liquidity_ratios')}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {(liquiditeRatios.length ? liquiditeRatios : ratios.slice(2)).map((ratio, i) => (
                              <div key={i} className="relative group">
                                <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-600 rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm" style={{ pointerEvents: 'none' }} />
                                <div className="relative bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20">
                                  <div className="text-white/60 text-sm">{ratioLabel(ratio)}</div>
                                  <div className="text-2xl font-bold text-white">{ratio.valeur}%</div>
                                  <div className="text-xs text-white/50 mt-1">{p('minimum_required', { seuil: ratio.seuil })}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                <button type="button" onClick={() => setRatiosExpandedSections((p) => ({ ...p, detail: !p.detail }))} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <BarChart3 size={20} className="text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{p('ratios_detail')}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={(e) => { e.stopPropagation(); handleExportRatios(); }} className="flex items-center gap-2 px-3 py-2 text-sm bg-green-500/20 text-green-200 border border-green-500/30 rounded-xl hover:bg-green-500/30 transition-colors">
                      <Download size={16} />
                      {p('export_pdf')}
                    </button>
                    {ratiosExpandedSections.detail ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </div>
                </button>
                <AnimatePresence>
                  {ratiosExpandedSections.detail && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="p-4 pt-0 space-y-3 max-h-64 overflow-y-auto">
                        {ratios.map((ratio, index) => (
                          <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10 flex justify-between flex-wrap gap-2">
                            <div>
                              <span className="text-white font-medium">{ratioLabel(ratio)}</span>
                              <p className="text-white/50 text-xs">{p('threshold_label', { seuil: ratio.seuil })}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-bold">{ratio.valeur}%</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${ratio.statut === 'conforme' ? 'bg-green-500/20 text-green-400' : ratio.statut === 'alerte' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                                {getRatioStatusLabel(ratio.statut)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <p className="text-blue-300 text-sm">
                  <strong>{p('note_prefix')}</strong> {p('ratios_modal_note')}
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end gap-2 flex-wrap">
              <button type="button" onClick={() => setShowRatiosPrudentiels(false)} className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-all font-medium">
                {p('close')}
              </button>
              <button type="button" onClick={() => goToTab('surveillance')} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold shadow-lg shadow-green-500/30 flex items-center gap-2">
                <Eye size={16} />
                {p('view_prudential_surveillance')}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showEtatsConsolides && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowEtatsConsolides(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '960px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <FileText className="h-6 w-6 text-pink-400" />
                {p('consolidated_statements_title')}
              </h2>
              <button type="button" onClick={() => setShowEtatsConsolides(false)} className="text-white/70 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1 space-y-4">
              <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                <button type="button" onClick={() => setEtatsExpandedSections((p) => ({ ...p, primary: !p.primary }))} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-3">
                    <Building size={20} className="text-indigo-400" />
                    <h3 className="text-lg font-semibold text-white">{p('consolidation_perimeter')}</h3>
                  </div>
                  {etatsExpandedSections.primary ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                </button>
                <AnimatePresence>
                  {etatsExpandedSections.primary && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="p-4 pt-0 space-y-4">
                        <div className="relative group">
                          <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm" style={{ pointerEvents: 'none' }} />
                          <div className="relative bg-gradient-to-r from-indigo-500/10 to-purple-500/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-white font-medium">{p('consolidated_entities')}</div>
                                <div className="text-white/60 text-sm">{p('subsidiaries_participations')}</div>
                              </div>
                              <div className="text-2xl font-bold text-white">{perimetre.length}</div>
                            </div>
                          </div>
                        </div>
                        {perimetre.length === 0 ? (
                          <p className="text-white/60 text-sm">{p('no_entities_to_consolidate')}</p>
                        ) : (
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {perimetre.map((e, i) => (
                              <div key={i} className="flex justify-between bg-white/5 rounded-lg p-2 text-sm">
                                <span className="text-white/80">{e.entite}</span>
                                <span className="text-pink-400">{e.pourcentageDetention}%</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {etatsConsolidesCards.map((card) => (
                            <div key={card.id} className="relative group">
                              <div className={`absolute -inset-0.5 bg-gradient-to-r ${card.glow} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                              <button
                                type="button"
                                onClick={() => handleSelectEtat(card.id)}
                                className={`relative w-full bg-white/10 backdrop-blur-lg rounded-3xl p-6 border text-left transition-all duration-300 hover:border-white/40 ${
                                  selectedEtatType === card.id ? 'border-blue-400/60 ring-2 ring-blue-500/30 shadow-lg shadow-blue-500/20' : 'border-white/20'
                                }`}
                              >
                                <div className="flex items-center space-x-3 mb-2">
                                  <card.icon className={`w-6 h-6 ${card.iconClass}`} />
                                  <div className="text-white font-semibold">{card.title}</div>
                                </div>
                                <div className="text-white/60 text-sm">{card.sub}</div>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                <button type="button" onClick={() => setEtatsExpandedSections((p) => ({ ...p, detail: !p.detail }))} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <Eye size={20} className="text-blue-400" />
                    <h3 className="text-lg font-semibold text-white truncate">
                      {selectedEtatType
                        ? etatsConsolidesCards.find((c) => c.id === selectedEtatType)?.title
                        : p('selected_statement_detail')}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedEtatType && (
                      <>
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleExportEtat(selectedEtatType); }} className="flex items-center gap-2 px-3 py-2 text-sm bg-green-500/20 text-green-200 border border-green-500/30 rounded-xl hover:bg-green-500/30 transition-colors">
                          <Download size={16} />
                          {p('export_pdf')}
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); goToTab('consolidation', selectedEtatType); }} className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-500/20 text-blue-200 border border-blue-500/30 rounded-xl hover:bg-blue-500/30 transition-colors">
                          <Eye size={16} />
                          {p('consolidation_tab_btn')}
                        </button>
                      </>
                    )}
                    {etatsExpandedSections.detail ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </div>
                </button>
                <AnimatePresence>
                  {etatsExpandedSections.detail && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="p-4 pt-0">{renderEtatDetailContent()}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <p className="text-blue-300 text-sm">
                  <strong>{p('note_prefix')}</strong> {p('consolidated_modal_note')}
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end gap-2 flex-wrap">
              <button type="button" onClick={() => setShowEtatsConsolides(false)} className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-all font-medium">
                {p('close')}
              </button>
              <button type="button" onClick={() => goToTab('consolidation', selectedEtatType)} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 text-white font-semibold shadow-lg shadow-pink-500/30 flex items-center gap-2">
                <Building size={16} />
                {p('view_consolidation')}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </>
  );
};

export default SecteursSpecialisesPage;
